/**
 * HELP002 SupportTicketDetail — right-sidebar value sync helpers.
 * Prefer stable numeric IDs over object identity for PrimeReact Dropdown matching.
 */

export function toStableUserId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    return toStableUserId(value.id ?? value.user_id);
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function userDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  return String(user?.employee?.name || user?.username || '').trim();
}

/**
 * Build Dropdown options for awaiting-user from mentions + awaiting_user fallback.
 * @returns {{ id: number, name: string }[]}
 */
export function buildAwaitingUserOptions(ticket) {
  const byId = new Map();
  const add = (user) => {
    const id = toStableUserId(user);
    if (id == null) return;
    const name = userDisplayName(user) || `#${id}`;
    if (!byId.has(id) || (name && name !== `#${id}`)) {
      byId.set(id, { id, name });
    }
  };
  (ticket?.mentions ?? []).forEach(add);
  if (ticket?.awaiting_user) add(ticket.awaiting_user);
  const awaitingId = toStableUserId(ticket?.awaiting_user_id);
  if (awaitingId != null && !byId.has(awaitingId)) {
    byId.set(awaitingId, { id: awaitingId, name: `#${awaitingId}` });
  }
  return Array.from(byId.values());
}

export function resolveAwaitingUserId(ticket) {
  return toStableUserId(ticket?.awaiting_user_id);
}

/**
 * Merge server ticket into previous detail without blanking confirmed sidebar fields
 * when the incoming payload omits them during a soft refresh.
 */
export function mergeSupportTicketDetail(prev, next, { replace = false } = {}) {
  if (!next || typeof next !== 'object') return prev ?? null;
  if (replace || !prev) return next;
  const merged = { ...prev, ...next };
  // Keep mentions if next omitted/empty while we still have confirmed ones and same ticket id
  if (
    toStableUserId(prev.id) === toStableUserId(next.id)
    && Array.isArray(prev.mentions)
    && prev.mentions.length > 0
    && (!Array.isArray(next.mentions) || next.mentions.length === 0)
    && next.awaiting_user_id != null
  ) {
    merged.mentions = prev.mentions;
  }
  if (
    toStableUserId(prev.id) === toStableUserId(next.id)
    && next.awaiting_user == null
    && prev.awaiting_user != null
    && toStableUserId(next.awaiting_user_id) === toStableUserId(prev.awaiting_user?.id)
  ) {
    merged.awaiting_user = prev.awaiting_user;
  }
  return merged;
}

/**
 * Narrow mutation sequencer: ignore stale responses for the same logical field.
 */
export function createSidebarMutationSequencer() {
  let seq = 0;
  return {
    begin() {
      seq += 1;
      return seq;
    },
    isCurrent(token) {
      return token === seq;
    },
    get current() {
      return seq;
    },
  };
}
